import { useAppSelector } from '../../redux/hooks';
import { activeSchemaIdSelector } from '../../redux/selectors/builder';
import usePermissionSchemas from '../PermissionControls/hooks/usePermissionSchemas';
import {
  ILlmConversationMessage,
  ILlmProcessConversationPromptMutation,
  ILlmProcessConversationPromptMutationVariables,
  ILlmResetConversationMutation,
  ILlmResetConversationMutationVariables,
  ILlmSchemaConversationQuery,
  ILlmSchemaConversationQueryVariables,
  LlmAbTestGroup,
  LlmPromptRequestType,
  PermissionKey,
} from '../../utils/apollo/graphqlSchema';
import SchemaDropdownMenu from '../../components/SchemaDropdownMenu';
import React, { KeyboardEventHandler, useCallback, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { LLM_SCHEMA_CONVERSATION } from '../../utils/apollo/llm/queries';
import toastMessage from '../../utils/toastMessage';
import { InputField, NumberInputField } from '../../components/Form';
import { PrimaryButton, SecondaryButton } from '../../components/Button';
import {
  LLM_PROCESS_CONVERSATION_PROMPT,
  LLM_RESET_CONVERSATION,
} from '../../utils/apollo/llm/mutations';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import { Loader } from '../../components/Loaders';
import { KubitLogoLoader } from '../../components/Chart/ChartLoader';
import { DebugLlmMessage } from './DebugLlmMessage';
import {
  MessagesWrapper,
  PageWrapper,
  PromptWrapper,
  RowWrapper,
} from './DebugLlmPageUI';
import { SingleSelectMenu } from '../../components/Dropdown';

const AB_TEST_OPTS = [
  {
    text: LlmAbTestGroup.Default,
    value: LlmAbTestGroup.Default,
  },
  {
    text: LlmAbTestGroup.Test,
    value: LlmAbTestGroup.Test,
  },
];

const REQUEST_TYPE_OPTS = Object.values(LlmPromptRequestType).map(
  (requestType) => {
    return {
      text: requestType,
      value: requestType,
    };
  },
);

const DEFAULT_CONTEXT_SIZE = 6; // 3 messages and 3 responses

const DebugLlmPage = () => {
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    defaultSchemaId: activeSchemaId,
    requestedPermissionKey: PermissionKey.AnalysisCreate, // TODO
  });

  const [prompt, setPrompt] = useState('');
  const [currentSchemaId, setCurrentSchemaId] = useState(selectedSchemaId);
  const [currentMessages, setCurrentMessages] = useState<
    ILlmConversationMessage[]
  >([]);
  const [maxContext, setMaxContext] = useState<number | null>(
    DEFAULT_CONTEXT_SIZE,
  );
  const [abTestGroup, setAbTestGroup] = useState(LlmAbTestGroup.Default);
  const [requestType, setRequestType] = useState(
    LlmPromptRequestType.CreateExpression,
  );

  const { loading, refetch } = useQuery<
    ILlmSchemaConversationQuery,
    ILlmSchemaConversationQueryVariables
  >(LLM_SCHEMA_CONVERSATION, {
    variables: {
      schemaId: currentSchemaId,
    },
    onCompleted: (data) => {
      const resp = data.llmSchemaConversation;
      if (!resp) {
        toastMessage({
          message: 'Failed to retrieve LLM conversation',
          type: 'error',
        });
        return;
      }
      setCurrentMessages(resp.messages.list);
    },
  });

  const onSchemaChange = useCallback(
    async (newSchema: string) => {
      await refetch({ schemaId: newSchema });
      setCurrentSchemaId(newSchema);
    },
    [refetch],
  );

  const [submitPrompt, { loading: submitting }] = useMutation<
    ILlmProcessConversationPromptMutation,
    ILlmProcessConversationPromptMutationVariables
  >(LLM_PROCESS_CONVERSATION_PROMPT);

  const onSubmit = useCallback(async () => {
    try {
      const resp = await submitPrompt({
        variables: {
          input: {
            schemaId: currentSchemaId,
            prompt,
            abTestGroup,
            maxContext: maxContext ?? 0,
            requestType,
          },
        },
      });
      if (printGraphqlErrors(resp.data?.llmProcessConversationPrompt?.errors)) {
        return;
      }
      const messages = resp.data?.llmProcessConversationPrompt?.entity;
      if (messages) {
        if (messages.list.length > 0) {
          setAbTestGroup(messages.list[0].abTestGroup);
        }
        setCurrentMessages((prev) => {
          return [...messages.list, ...prev];
        });
      }
      setPrompt('');
    } catch (err) {
      toastMessage({ message: `Unexpected error ${err}`, type: 'error' });
    }
  }, [
    prompt,
    submitPrompt,
    currentSchemaId,
    maxContext,
    abTestGroup,
    requestType,
  ]);

  const [submitReset, { loading: resetting }] = useMutation<
    ILlmResetConversationMutation,
    ILlmResetConversationMutationVariables
  >(LLM_RESET_CONVERSATION);

  const onReset = useCallback(async () => {
    const res = await submitReset({
      variables: {
        input: {
          schemaId: currentSchemaId,
        },
      },
    });
    if (printGraphqlErrors(res.data?.llmResetConversation?.errors)) {
      return;
    }
    await refetch();
  }, [submitReset, refetch, currentSchemaId]);

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        onSubmit();
      }
    },
    [onSubmit],
  );

  return (
    <PageWrapper>
      <RowWrapper>
        <SchemaDropdownMenu
          value={currentSchemaId}
          onChange={onSchemaChange}
          permittedSchemaIds={permittedSchemaIds}
        />
        <div>AB Test Group</div>
        <SingleSelectMenu
          options={AB_TEST_OPTS}
          onChange={(v) => setAbTestGroup(v as LlmAbTestGroup)}
          value={abTestGroup}
        />
        <div>Request Type</div>
        <SingleSelectMenu
          options={REQUEST_TYPE_OPTS}
          onChange={(v) => setRequestType(v as LlmPromptRequestType)}
          value={requestType}
        />
      </RowWrapper>
      {loading || resetting ? (
        <RowWrapper>
          <Loader>Loading conversation</Loader>
        </RowWrapper>
      ) : (
        <>
          <RowWrapper>
            <PromptWrapper>
              <InputField
                disabled={submitting}
                onKeyDown={onInputKeyDown}
                value={prompt}
                onChange={setPrompt}
              />
            </PromptWrapper>
            <div>
              <PrimaryButton onClick={onSubmit}>Submit</PrimaryButton>
            </div>
            <div>
              <SecondaryButton isOutlined={true} onClick={onReset}>
                Delete All Messages
              </SecondaryButton>
            </div>
            <div>Max Context:</div>
            <div>
              <NumberInputField value={maxContext} onChange={setMaxContext} />
            </div>
          </RowWrapper>
          {submitting && (
            <RowWrapper>
              <KubitLogoLoader height={100} isGradient={true}>
                Awaiting AI response
              </KubitLogoLoader>
            </RowWrapper>
          )}
          <MessagesWrapper>
            {currentMessages.map((message, index) => {
              return (
                <div key={`msg-${message.id}-${message.createdAt}`}>
                  {index === (maxContext ?? 0) && (
                    <div>
                      <div>^^^^ END OF LLM CHAT CONTEXT ^^^^</div>
                      <hr />
                    </div>
                  )}
                  <DebugLlmMessage message={message} />
                </div>
              );
            })}
          </MessagesWrapper>
        </>
      )}
    </PageWrapper>
  );
};

export default DebugLlmPage;
